import isEmpty from 'lodash/isEmpty'
import React, { useState, Suspense } from 'react'
import { BankrateOffer } from '@bankrate/mortgage-types'

import { useStoreActions, useStoreState } from 'store'
import TableHead from 'components/rate-table/TableHead'
import { ShimmerRow } from 'components/rate-table/row/ShimmerRow'
import { Tab } from 'components/offer-details/OfferDetailsModal'
import MoreOffersButton from 'components/rate-table/MoreOffersButton'
import ImportantInformation from 'components/rate-table/ImportantInformation'
import { retry } from '../../utils/retry'
import CTAModal from './row/CTAModal'

const PaidRow = React.lazy(() => retry(() => import(/* webpackChunkName: "PaidRow" */ './row/PaidRow')))
const SponsoredRow = React.lazy(() => retry(() => import(/* webpackChunkName: "SponsoredRow" */ './row/SponsoredRow')))
const EditorialRow = React.lazy(() => retry(() => import(/* webpackChunkName: "EditorialRow" */ './row/EditorialRow')))
const ExtraOffersRow = React.lazy(() =>
  retry(() => import(/* webpackChunkName: "ExtraOffersRow" */ './row/ExtraOffersRow'))
)
const RateForItRow = React.lazy(() => retry(() => import(/* webpackChunkName: "RateForItRow" */ './row/RateForItRow')))
const HelpDeskRow = React.lazy(() => retry(() => import(/* webpackChunkName: "HelpDeskRow" */ './row/HelpDeskRow')))
const OfferDetailsModal = React.lazy(() =>
  retry(() => import(/* webpackChunkName: "OfferDetailsModal" */ 'components/offer-details/OfferDetailsModal'))
)
const BrandedRowV2 = React.lazy(() => retry(() => import(/* webpackChunkName: "BrandedRowV2" */ './row/BrandedRowV2')))
const EditorialDisclosureRow = React.lazy(() =>
  retry(() => import(/* webpackChunkName: "RateForItRow" */ './row/EditorialDisclosureRow'))
)

export default function RateTable(): JSX.Element {
  const [visible, setVisible] = useState(false)
  const [selectedOffer, setSelectedOffer] = useState({} as BankrateOffer)
  const [activeTab, setActiveTab] = useState(Tab.Reviews)
  const [rowPosition, setRowPosition] = useState(0)
  const flags = useStoreState(state => state.featureFlags.flags)

  const offerDetailsHandler = (offer: BankrateOffer, rowPosition: number, tab?: Tab) => {
    setVisible(true)
    setSelectedOffer(offer)
    setActiveTab(tab ?? Tab.Details)
    setRowPosition(rowPosition)
  }

  const { isLoading } = useStoreState(state => state.offers)
  const { offers, ctaModal } = useStoreState(state => state.offers)
  const { setCtaModal } = useStoreActions(actions => actions.offers)

  const showTable = isLoading || (!isLoading && offers.length)

  return showTable ? (
    <>
      <table className="Table Table--borderedRows rate-table mb-0 SplitDisplay-table">
        <TableHead />
        <tbody>
          {!isLoading ? (
            offers.map((offer, index) => (
              <Suspense key={index} fallback={<ShimmerRow />}>
                <OfferRow index={index} offer={offer} detailsHandler={offerDetailsHandler} flags={flags} />
              </Suspense>
            ))
          ) : (
            <ShimmerRow />
          )}
        </tbody>
      </table>

      <MoreOffersButton />

      {!isEmpty(selectedOffer) && (
        <Suspense fallback={<div />}>
          <OfferDetailsModal
            offer={selectedOffer}
            visible={visible}
            setVisible={setVisible}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            rowPosition={rowPosition}
          />
        </Suspense>
      )}

      {ctaModal && <CTAModal modalData={ctaModal} onClose={() => setCtaModal(null)} />}

      {!isLoading ? <ImportantInformation /> : <></>}
    </>
  ) : (
    <></>
  )
}

const OfferRow = ({
  offer,
  detailsHandler,
  index,
  flags,
}: {
  offer: BankrateOffer
  detailsHandler: (offer: BankrateOffer, rowPosition: number, tab?: Tab | undefined) => void
  index: number
  flags: {
    [key: string]: unknown
  }
}) => {
  const type = offer.type as BankrateOffer['type'] | 'rate-for-it' | 'branded' | 'help-desk' | 'editorial-disclosure'

  let className = ''
  if (flags['testMode'] && offer.tags?.includes('testMode')) className += ' bg-red-light'

  switch (type) {
    case 'editorial':
      return <EditorialRow index={index} offer={offer} />

    case 'rate':
      return <PaidRow index={index} offer={offer} offerDetailsHandler={detailsHandler} className={className} />

    case 'sponsored':
      return <SponsoredRow index={index} offer={offer} className={className} />

    case 'low-coverage-extra-offers' as string:
      return <ExtraOffersRow index={index} />

    case 'branded':
      return <BrandedRowV2 index={index} />

    case 'rate-for-it':
      return <RateForItRow index={index} />

    case 'help-desk':
      return flags['showHelpDesk'] ? <HelpDeskRow /> : <></>

    case 'editorial-disclosure':
      return <EditorialDisclosureRow index={index} />
  }

  return <></>
}
